/**
 * useBatchMutations - Mutations for batch submission
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { createBatchV2, generateMatrixBatch, type MatrixConfig } from '@/lib/api/batch';

interface MutationCallbacks {
  onSuccess?: () => void;
  resetForm: () => void;
}

export function useBatchMutations({ onSuccess, resetForm }: MutationCallbacks) {
  const queryClient = useQueryClient();

  function handleSuccess(data: { success: boolean; total_images?: number; error?: string }) {
    if (data.success) {
      toast.success(`Batch erstellt! ${data.total_images} Bilder werden generiert.`);
      queryClient.invalidateQueries({ queryKey: ['batch', 'runs'] });
      resetForm();
      onSuccess?.();
    } else {
      toast.error(data.error || 'Fehler beim Erstellen des Batches');
    }
  }

  function handleError() {
    toast.error('Netzwerkfehler beim Erstellen des Batches');
  }

  const unifiedMutation = useMutation({
    mutationFn: createBatchV2,
    onSuccess: handleSuccess,
    onError: handleError,
  });

  const matrixMutation = useMutation({
    mutationFn: (data: { files: File[]; imageIds: string[]; config: MatrixConfig }) =>
      generateMatrixBatch(data.files, data.imageIds, data.config),
    onSuccess: handleSuccess,
    onError: handleError,
  });

  return { unifiedMutation, matrixMutation };
}
